import type { IDesign, IText, ITrackItemAndDetails } from "@designcombo/types";
import { DEFAULT_FONT, SECONDARY_FONT } from "./fonts";

/**
 * load custom fonts + selected by the user from google fonts
 * @returns promise of all custome fonts
 */
export const ensureFont = async (
	allGoogleFonts: any[],
	design: IDesign,
	staticFile: (url: string) => string,
) => {
	const trackItemsMap = design.trackItemsMap as Record<
		string,
		ITrackItemAndDetails
	>;
	const trackFonts: string[] = [];
	for (const key in trackItemsMap) {
		const trackItem = trackItemsMap[key] as IText;
		if (
			trackItem.type === "text" ||
			(trackItem.type === "caption" &&
				trackItem.details.fontFamily &&
				trackItem.details.fontUrl)
		) {
			trackFonts.push(trackItem.details.fontFamily);
		}
	}

	// Extract fonts from track styles
	const trackStyleFonts = extractFontsFromTrackStyles(design.tracks);
	trackStyleFonts.push(...trackFonts, DEFAULT_FONT.postScriptName, SECONDARY_FONT.postScriptName);
	console.log("trackStyleFonts", trackStyleFonts);
	// Check trackStyleFonts against availableFonts and googleFonts
	new LazyFontLoader(allGoogleFonts, staticFile);
	await LazyFontLoader.loadFonts(trackStyleFonts);
};

// Function to extract fonts from track styles
const extractFontsFromTrackStyles = (tracks: any[]): string[] => {
	const fonts: string[] = [];

	tracks?.forEach((track) => {
		track?.styles?.forEach((style: any) => {
			if (style.property === "fontFamily") {
				// Check value
				if (style.value && typeof style.value === "string") {
					fonts.push(style.value);
				}
				// Check active.value
				if (
					style.active?.value &&
					typeof style.active.value === "string"
				) {
					fonts.push(style.active.value);
				}
				// Check past.value
				if (style.past?.value && typeof style.past.value === "string") {
					fonts.push(style.past.value);
				}
			}
		});
	});

	// Remove duplicates
	return Array.from(new Set(fonts));
};

export const loadFonts = (fonts: { name: string; url: string }[]) => {
	const promisesList = fonts.map((font) => {
		return new FontFace(font.name, `url(${font.url})`)
			.load()
			.catch((err) => err);
	});
	return new Promise((resolve, reject) => {
		Promise.all(promisesList)
			.then((res) => {
				res.forEach((uniqueFont) => {
					if (uniqueFont?.family) {
						document.fonts.add(uniqueFont);
						resolve(true);
					}
				});
			})
			.catch((err) => reject(err));
	});
};

// new

// Custom lazy font loading utility for the editor
interface IGoogleFont{
    getInfo: () => any;
    fontFamily: string;
    loadFont: () => Promise<any>;
}
class LazyFontLoader {
	static loadingFonts = new Set<string>();
	static loadedFonts = new Set<string>();
	static fontLoadPromises = new Map<string, Promise<void>>();
	static cachedGoogleFonts:
		| {
				fontFamily: string;
				importName: string;
				load: () => Promise<IGoogleFont>;
		  }[]
		| null = null;
	static customFonts = [
		{ 
			name: DEFAULT_FONT.postScriptName, 
			family: DEFAULT_FONT.family, 
			preview: DEFAULT_FONT.preview,
			type: 'custom' as const
		},
		{
			name: SECONDARY_FONT.postScriptName, 
			family: SECONDARY_FONT.family, 
			preview: SECONDARY_FONT.preview,
			type: 'custom' as const
		}
	]
	static staticFile: (url: string) => string;

	static allFonts: ({
		name: string;
		family: string;
		preview: string;
		type: "google";
	} | {
		name: string;
		family: string;
		preview: string;
		type: "custom";
	})[] = []

	constructor(
		allGoogleFonts: {
			fontFamily: string;
			importName: string;
			load: () => Promise<any>;
		}[],
		staticFile: (url: string) => string,
	) {
		LazyFontLoader.cachedGoogleFonts = allGoogleFonts;
		LazyFontLoader.staticFile = staticFile;
		LazyFontLoader.allFonts = [
			// Custom fonts first
			{ 
				name: DEFAULT_FONT.postScriptName, 
				family: DEFAULT_FONT.family, 
				preview: DEFAULT_FONT.preview,
				type: 'custom' as const
			},
			{ 
				name: SECONDARY_FONT.postScriptName, 
				family: SECONDARY_FONT.family, 
				preview: SECONDARY_FONT.preview,
				type: 'custom' as const
			},
			...(allGoogleFonts || []).map(font => ({
				name: font.importName,
				family: font.fontFamily,
				preview: font.fontFamily,
				type: 'google' as const
			}))
		];
	}

	static getGoogleFonts() {
		return LazyFontLoader.cachedGoogleFonts;
	}

	static async loadGoogleFont(fontName: string): Promise<void> {
		if (LazyFontLoader.loadedFonts.has(fontName)) return;
		if (LazyFontLoader.loadingFonts.has(fontName)) {
			return LazyFontLoader.fontLoadPromises.get(fontName);
		}

		LazyFontLoader.loadingFonts.add(fontName);

		const loadPromise = new Promise<void>((resolve) => {
			(async () => {
				try {
					const allGoogleFonts = LazyFontLoader.cachedGoogleFonts!;
					const font = allGoogleFonts.find(
						(f) => f.importName === fontName,
					);

					if (font?.load) {
						const loaded = await font.load();
						await loaded?.loadFont?.();
						LazyFontLoader.loadedFonts.add(fontName);
					}
					resolve();
				} catch (error) {
					console.error(`Failed to load font ${fontName}:`, error);
					resolve();
				} finally {
					LazyFontLoader.loadingFonts.delete(fontName);
					LazyFontLoader.fontLoadPromises.delete(fontName);
				}
			})();
		});

		LazyFontLoader.fontLoadPromises.set(fontName, loadPromise);
		return loadPromise;
	}

	static async loadCustomFont(fontName: string, fontUrl: string): Promise<void> {
		if (LazyFontLoader.loadedFonts.has(fontName)) return;
		if (LazyFontLoader.loadingFonts.has(fontName)) {
			return LazyFontLoader.fontLoadPromises.get(fontName);
		}

		LazyFontLoader.loadingFonts.add(fontName);

		const loadPromise = new Promise<void>((resolve) => {
			try {
				if (typeof window !== "undefined" && "FontFace" in window) {
					const fontFace = new FontFace(fontName, `url(${fontUrl})`);
					fontFace
						.load()
						.then(() => {
							document.fonts.add(fontFace);
							LazyFontLoader.loadedFonts.add(fontName);
							resolve();
						})
						.catch((error) => {
							console.error(
								`Failed to load custom font ${fontName}:`,
								error,
							);
							resolve();
						});
				} else {
					resolve();
				}
			} catch (error) {
				console.error(`Failed to load custom font ${fontName}:`, error);
				resolve();
			} finally {
				LazyFontLoader.loadingFonts.delete(fontName);
				LazyFontLoader.fontLoadPromises.delete(fontName);
			}
		});

		LazyFontLoader.fontLoadPromises.set(fontName, loadPromise);
		return loadPromise;
	}

	static isFontLoaded(fontName: string): boolean {
		return LazyFontLoader.loadedFonts.has(fontName);
	}

	static isFontLoading(fontName: string): boolean {
		return LazyFontLoader.loadingFonts.has(fontName);
	}

	static async loadFonts(_fontsToLoad: string[]) {
		const fontsToLoad = _fontsToLoad
			.map((fontName) => LazyFontLoader.allFonts.find((f) => f.name === fontName || f.family === fontName))
			.filter(Boolean) as Array<{
			name: string;
			type: "google" | "custom";
		}>;


		try {
			// Load all fonts simultaneously
			await Promise.allSettled(
				fontsToLoad.map(async (font) => {
					if (
						LazyFontLoader.isFontLoaded(font.name) ||
						LazyFontLoader.isFontLoading(font.name)
					) {
						return;
					}

					try {
						if (font.type === "google") {
							await LazyFontLoader.loadGoogleFont(font.name);
						} else {
							// For custom fonts, we need the URL
							if (font.name === DEFAULT_FONT.postScriptName) {
								await LazyFontLoader.loadCustomFont(
									font.name,
									LazyFontLoader.staticFile(DEFAULT_FONT.url),
								);
							} else if (
								font.name === SECONDARY_FONT.postScriptName
							) {
								await LazyFontLoader.loadCustomFont(
									font.name,
									LazyFontLoader.staticFile(SECONDARY_FONT.url),
								);
							}
						}
					} catch (error) {
						console.error(
							`Failed to load font ${font.name}:`,
							error,
						);
					}
				}),
			);
			// Add all fonts to loading state
			LazyFontLoader.loadingFonts = new Set([
				...Array.from(LazyFontLoader.loadingFonts), 
				...fontsToLoad.map((f) => f.name)
			]);

		} finally {
			// Remove all fonts from loading state
			// LazyFontLoader.loadingFonts.delete(...fontsToLoad.map((f) => f.name));
			fontsToLoad.forEach((font) => LazyFontLoader.loadingFonts.delete(font.name))
			// setLoadingFonts((prev) => {
			// 	const newSet = new Set(prev);
			// 	fontsToLoad.forEach((font) => newSet.delete(font.name));
			// 	return newSet;
			// });
		}
	}
}

// Global font loader instance
export { LazyFontLoader };
